import { DefaultModal } from "@containers/common/modals/default-modal/default-modal";
import { MessageTemplateContentForm } from "@containers/modals/add-message-template-modal/message-template-content-form/message-template-content-form";
import { MessageTemplateMetadataForm } from "@containers/modals/add-message-template-modal/message-template-metadata-form/message-template-metadata-form";
import { MessageTemplateVariablesForm } from "@containers/modals/add-message-template-modal/message-template-variables-form/message-template-variables-form";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { MultiLanguageWrapper } from "@iddataweb/idw-common-ui/types";
import { MessageTemplate, FormattedMessageTemplate, SelfServiceVerificationWhitelabeledEmails } from "@idw-types/data/message-template";
import { IDWSession } from "@idw-types/data/idw-session";
import { AxnCompany } from "@idw-types/data/reference-data/axn";
import { createMessageTemplateReduxRequest } from "@redux-requests/message-templates";
import { getInitialMessageTemplate, removeUnusedVariablesFromMessageTemplateLanguageFiles } from "@utils/message-template/message-template-util";
import { getMessageTemplateValidationErrors } from "@utils/validation/message-template/message-template-validation-util";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

type AddMessageTemplateModalProps = {
    session: IDWSession;
    onModalClose: () => void;
    messageTemplates: Array<FormattedMessageTemplate>;
    axnCompanies: Array<AxnCompany>;
    selfServiceVerificationWhiteLabeledEmails: Array<SelfServiceVerificationWhitelabeledEmails>;
    serviceOrganizationId: string;
    addToReferenceData: boolean;
    onMessageTemplateCreated?: (messageTemplate: MessageTemplate) => void;
};

export const AddMessageTemplateModal = (props: AddMessageTemplateModalProps) => {
    const dispatch = useDispatch();
    const {
        session,
        onModalClose,
        axnCompanies,
        selfServiceVerificationWhiteLabeledEmails,
        messageTemplates,
        onMessageTemplateCreated,
        serviceOrganizationId,
        addToReferenceData
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);
    const [modalStep, setModalStep] = useState(0);

    const [selectedLanguage, setSelectedLanguage] = useState<LocaleTypes>(LocaleTypes.ENGLISH);
    const [requestInProgress, setRequestInProgress] = useState(false);
    const [state, setState] = useState<FormattedMessageTemplate>(getInitialMessageTemplate(
        axnCompanies,
        false,
        session,
    ));

    useEffect(() => {
        const languages: Partial<MultiLanguageWrapper<{ [key: string]: any }>> = {};
        if (state.languages) {
            (Object.keys(state.languages) as Array<LocaleTypes>).forEach(locale => {
                languages[locale] = state.languages[locale] || {};
            });
        }

        setState({
            ...state,
            languages
        });
    }, [state.template]);

    const handleSubmitOnStep0 = (event: FormEvent) => {
        event.preventDefault();
        setModalStep(1);
    };

    const handleSubmitOnStep1 = (event: FormEvent) => {
        event.preventDefault();
        setModalStep(2);
    };

    const handleSubmitOnStep2 = async (event: FormEvent) => {
        event.preventDefault();
        setRequestInProgress(true);
        const newLanguageFiles = removeUnusedVariablesFromMessageTemplateLanguageFiles(
            state.template,
            state.languages,
            state.type,
        );

        const newStringifiedLanguageFiles: Partial<MultiLanguageWrapper<string>> = {};
        (Object.keys(newLanguageFiles) as Array<LocaleTypes>).forEach((key => {
            newStringifiedLanguageFiles[key] = JSON.stringify(newLanguageFiles[key]);
        }));

        const newMessageTemplate = await createMessageTemplateReduxRequest(dispatch)(
            state.template,
            state.name,
            state.company?.id,
            state.sender,  
            state.type,
            newStringifiedLanguageFiles,
            axnCompanies,
            addToReferenceData
        );
        if (newMessageTemplate) {
            if (onMessageTemplateCreated) {
                onMessageTemplateCreated(newMessageTemplate);
            }
            onModalClose();
        } else {
            setRequestInProgress(false);
        }
    }

    const handleBackClickedOnStep1 = () => {
        setModalStep(0);
    };

    const handleBackClickedOnStep2 = () => {
        setModalStep(1);
    };

    const validationErrors = useMemo(() => {
        return getMessageTemplateValidationErrors(state);
    }, [
        state.name, state.languages,
        state.template, state.templateAvailabilityLevel,
        state.company
    ]);

    return (
        <DefaultModal
            automationId="add-message-template-modal"
            ariaLabelledBy="add-message-template-title"
            onHide={onModalClose}
            backdrop={"static"}
            keyboard={false}
            isMaxWidth={modalStep > 0}
        >
            {modalStep === 0 && (
                <MessageTemplateMetadataForm
                    session={session}
                    axnCompanies={axnCompanies}
                    selfServiceVerificationWhiteLabeledEmails= {selfServiceVerificationWhiteLabeledEmails}
                    validationErrors={validationErrors.generalInfo}
                    messageTemplates={messageTemplates}
                    serviceOrganizationId={serviceOrganizationId}
                    handleSubmit={handleSubmitOnStep0}
                    labelWidth={labelWidth}
                    onModalClose={onModalClose}
                    setState={setState}
                    state={state}
                    requestInProgress={requestInProgress}
                />
            )}
            {modalStep === 1 && (
                <MessageTemplateContentForm
                    state={state}
                    requestInProgress={requestInProgress}
                    selectedLanguage={selectedLanguage}
                    setSelectedLanguage={setSelectedLanguage}
                    validationErrors={validationErrors.content}
                    setState={setState}
                    onBackClicked={handleBackClickedOnStep1}
                    handleSubmit={handleSubmitOnStep1}
                />
            )}
            {modalStep === 2 && (
                <MessageTemplateVariablesForm
                    state={state}
                    requestInProgress={requestInProgress}
                    selectedLanguage={selectedLanguage}
                    validationErrors={validationErrors.variables}
                    setSelectedLanguage={setSelectedLanguage}
                    setState={setState}
                    onBackClicked={handleBackClickedOnStep2}
                    handleSubmit={handleSubmitOnStep2}
                />
            )}
        </DefaultModal>
    );
};